import React from 'react';
import { Dialog } from '../reusable';
import { Hash, MapPin, CreditCard, Users, Landmark } from 'lucide-react';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL');
};

const Field = ({ label, value, isDarkMode }) => {
  const muted = isDarkMode ? '#a1a1aa' : '#64748b';
  const text = isDarkMode ? '#f4f4f5' : '#0f172a';
  return (
    <div style={{ marginBottom: '10px' }}>
      <div
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: muted,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '14px', color: text, marginTop: '2px', wordBreak: 'break-word' }}>
        {value || '—'}
      </div>
    </div>
  );
};

const listCardStyle = (isDarkMode) => ({
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
});

const SupplierDetailModal = ({
  isOpen,
  onClose,
  supplier,
  contacts = [],
  isDarkMode = false,
  onEdit,
}) => {
  if (!supplier) return null;

  const initial = String(supplier.name || '?').trim().charAt(0).toUpperCase() || '?';
  const paymentConditions = Array.isArray(supplier.payment_conditions)
    ? supplier.payment_conditions
    : [];
  const addresses = Array.isArray(supplier.addresses) ? supplier.addresses : [];
  const bankAccounts = Array.isArray(supplier.bank_accounts) ? supplier.bank_accounts : [];
  const status = String(supplier.status || 'Activo');
  const isActiveStatus = status.toLowerCase() === 'activo';

  const body = (
    <div style={{ padding: '0 20px 20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
        }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: isDarkMode ? '#27272a' : '#f1f5f9',
            display: 'grid',
            placeItems: 'center',
            fontSize: '22px',
            fontWeight: 800,
            color: isDarkMode ? '#fafafa' : '#0f172a',
            flexShrink: 0,
          }}
        >
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            Código · {supplier.supplier_code || '—'}
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '18px',
              fontWeight: 700,
              color: isDarkMode ? '#f4f4f5' : '#0f172a',
            }}
          >
            {supplier.name}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: isDarkMode ? '#d4d4d8' : '#475569' }}>
            {supplier.supplier_type || 'Sin tipo'}
            <span
              style={{
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '11px',
                background: isActiveStatus ? '#dcfce7' : isDarkMode ? '#3f3f46' : '#f4f4f5',
                color: isActiveStatus ? '#166534' : isDarkMode ? '#d4d4d8' : '#52525b',
              }}
            >
              {status}
            </span>
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          { label: 'Compras', value: String(supplier.purchases_count ?? 0) },
          { label: 'Última compra', value: formatDate(supplier.last_purchase_at) },
          { label: 'Monto total', value: formatMoney(supplier.purchases_amount) },
          { label: 'Contactos', value: String(supplier.contacts_count ?? contacts.length ?? 0) },
        ].map((metric) => (
          <div
            key={metric.label}
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
              background: isDarkMode ? '#18181b' : '#f8fafc',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: isDarkMode ? '#a1a1aa' : '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {metric.label}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 18,
                fontWeight: 700,
                color: isDarkMode ? '#f4f4f5' : '#0f172a',
              }}
            >
              {metric.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px 24px',
        }}
      >
        <div>
          <h4
            style={{
              margin: '0 0 10px',
              fontSize: '13px',
              color: isDarkMode ? '#e4e4e7' : '#334155',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Hash size={16} /> Identificación
          </h4>
          <Field label="RUT" value={supplier.rut} isDarkMode={isDarkMode} />
          <Field label="Razón social" value={supplier.business_name} isDarkMode={isDarkMode} />
          <Field label="Tipo" value={supplier.supplier_type} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4
            style={{
              margin: '0 0 10px',
              fontSize: '13px',
              color: isDarkMode ? '#e4e4e7' : '#334155',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <CreditCard size={16} /> Condiciones de pago
          </h4>
          {paymentConditions.length === 0 ? (
            <Field label="Condiciones" value="—" isDarkMode={isDarkMode} />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {paymentConditions.map((term) => (
                <span
                  key={term}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 999,
                    fontSize: 12,
                    background: isDarkMode ? '#27272a' : '#f1f5f9',
                    color: isDarkMode ? '#e4e4e7' : '#334155',
                  }}
                >
                  {term}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <h4
          style={{
            margin: '0 0 10px',
            fontSize: '13px',
            color: isDarkMode ? '#e4e4e7' : '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <MapPin size={16} /> Direcciones
        </h4>
        {addresses.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            {supplier.address ? supplier.address : 'Sin direcciones asociadas.'}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {addresses.map((row) => (
              <div key={row.id || `${row.address_type}-${row.address}`} style={listCardStyle(isDarkMode)}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: isDarkMode ? '#f4f4f5' : '#0f172a',
                  }}
                >
                  {row.address_type || 'Dirección'}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
                  {[row.address, row.commune, row.city, row.region, row.country]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 22 }}>
        <h4
          style={{
            margin: '0 0 10px',
            fontSize: '13px',
            color: isDarkMode ? '#e4e4e7' : '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Landmark size={16} /> Cuentas bancarias
        </h4>
        {bankAccounts.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            Sin cuentas bancarias asociadas.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {bankAccounts.map((row) => {
              const active = String(row.status || 'Activo').toLowerCase() === 'activo';
              return (
                <div
                  key={row.id || `${row.bank_name}-${row.account_number}`}
                  style={listCardStyle(isDarkMode)}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: isDarkMode ? '#f4f4f5' : '#0f172a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span>{row.bank_name || 'Banco'}</span>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 500,
                        background: active ? '#dcfce7' : isDarkMode ? '#3f3f46' : '#f4f4f5',
                        color: active ? '#166534' : isDarkMode ? '#d4d4d8' : '#52525b',
                      }}
                    >
                      {row.status || 'Activo'}
                    </span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
                    {[
                      row.account_type,
                      row.account_number,
                      row.currency,
                      row.account_holder,
                      row.holder_rut,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 22 }}>
        <h4
          style={{
            margin: '0 0 10px',
            fontSize: '13px',
            color: isDarkMode ? '#e4e4e7' : '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Users size={16} /> Contactos
        </h4>
        {contacts.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            Sin contactos asociados.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {contacts.map((contact) => (
              <div key={contact.id || contact.full_name} style={listCardStyle(isDarkMode)}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: isDarkMode ? '#f4f4f5' : '#0f172a',
                  }}
                >
                  {contact.full_name}
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 500,
                      color: isDarkMode ? '#a1a1aa' : '#64748b',
                    }}
                  >
                    {contact.status || 'Activo'}
                  </span>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
                  {[contact.role, contact.email, contact.phone].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {onEdit ? (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            type="button"
            onClick={() => onEdit(supplier)}
            style={{
              height: '40px',
              padding: '0 18px',
              borderRadius: '8px',
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Editar proveedor
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Ficha de proveedor"
      subtitle="Resumen del maestro de adquisiciones"
      isDarkMode={isDarkMode}
      size="lg"
    >
      {body}
    </Dialog>
  );
};

export default SupplierDetailModal;
